import React from 'react';

// Função para renderizar campo JSON
const renderJsonField = (label, value, type = 'string') => {
  if (value === null || value === undefined || value === '') {
    return null; // Não renderizar campos vazios
  }

  // Verificar se é um objeto (não array)
  if (typeof value === 'object' && !Array.isArray(value)) {
    return (
      <div className="flex justify-between items-center py-1 border-b border-gray-100">
        <span className="text-sm font-medium text-gray-600">{label}:</span>
        <span className="text-sm text-gray-400 italic">Objeto (ver comissões)</span>
      </div>
    );
  }

  // Verificar se é um array
  if (Array.isArray(value)) {
    return (
      <div className="flex justify-between items-center py-1 border-b border-gray-100">
        <span className="text-sm font-medium text-gray-600">{label}:</span>
        <span className="text-sm text-gray-400 italic">Array ({value.length} items)</span>
      </div>
    );
  }

  let displayValue = value;
  let valueClass = 'text-gray-800';

  if (type === 'number' || typeof value === 'number') {
    valueClass = 'text-blue-600 font-mono';
  } else if (type === 'boolean' || typeof value === 'boolean') {
    valueClass = value ? 'text-green-600' : 'text-red-600';
    displayValue = value ? 'Sim' : 'Não';
  } else if (type === 'url') {
    valueClass = 'text-blue-500 hover:text-blue-700';
    // Verificar se é uma URL válida
    const isValidUrl = value && (value.startsWith('http://') || value.startsWith('https://'));
    if (isValidUrl) {
      return (
        <div className="flex justify-between items-center py-1 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-600">{label}:</span>
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer"
            className={`text-sm ${valueClass} max-w-xs break-all hover:underline`}
            title={value}
          >
            {value}
          </a>
        </div>
      );
    }
  } else if (type === 'date') {
    valueClass = 'text-purple-600';
  } else if (type === 'currency') {
    valueClass = 'text-green-600 font-semibold';
  } else if (type === 'status') {
    valueClass = 'text-orange-600 font-medium';
  }

  return (
    <div className="flex justify-between items-center py-1 border-b border-gray-100">
      <span className="text-sm font-medium text-gray-600">{label}:</span>
      <span className={`text-sm ${valueClass} max-w-xs break-all`} title={String(value)}>
        {displayValue}
      </span>
    </div>
  );
};

// Função para renderizar seção de campos
const renderFieldSection = (title, fields, data) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-4">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
        <h4 className="font-semibold text-gray-800">{title}</h4>
      </div>
      <div className="p-4 space-y-1">
        {fields.map((field, index) => {
          // Lidar com campos aninhados (ex: comissoesCobranca.nome)
          let value;
          if (field.name.includes('.')) {
            const parts = field.name.split('.');
            value = data[parts[0]]?.[parts[1]];
          } else {
            value = data[field.name];
          }
          return (
            <div key={`${field.name}-${index}`}>
              {renderJsonField(field.label, value, field.type)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Função para renderizar comissões
const renderComissoes = (comissoes) => {
  if (!comissoes || !Array.isArray(comissoes) || comissoes.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 mb-4">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
          <h4 className="font-semibold text-gray-800">Comissões</h4>
        </div>
        <div className="p-4">
          <span className="text-sm text-gray-400 italic">Nenhuma comissão configurada</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-4">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
        <h4 className="font-semibold text-gray-800">Comissões ({comissoes.length})</h4>
      </div>
      <div className="p-4 space-y-3">
        {comissoes.map((comissao, index) => {
          // Verificar se comissao é válido
          if (!comissao || typeof comissao !== 'object') {
            return (
              <div key={index} className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-red-600 text-sm">Comissão inválida</p>
              </div>
            );
          }
          
          return (
            <div key={index} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Nome:</span>
                  <p className="text-gray-700 font-medium">{comissao.nome || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Porcentagem:</span>
                  <p className="text-blue-600 font-semibold">{comissao.porcentagem || 0}%</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Responsável:</span>
                  <p className="text-gray-700">
                    {comissao.comissionadoResponsavel ? 'Sim' : 'Não'}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const JsonFieldRenderer = ({ data }) => {
  // Log para debug
  console.log('📊 JsonFieldRenderer - Dados recebidos:', data);
  console.log('📊 JsonFieldRenderer - Tipo de dados:', typeof data);
  console.log('📊 JsonFieldRenderer - Chaves do objeto:', data ? Object.keys(data) : 'null');

  // Se não tem dados, mostrar mensagem
  if (!data || typeof data !== 'object') {
    return (
      <div className="bg-red-50 p-4 rounded-lg border border-red-200">
        <p className="text-red-600 text-sm">⚠️ Dados inválidos ou não disponíveis</p>
        <p className="text-red-500 text-xs mt-2">Tipo recebido: {typeof data}</p>
      </div>
    );
  }

  // Coletar todos os campos conhecidos para evitar duplicação
  const knownFields = new Set([
    'nomePagador', 'cobrancaID', 'id', 'documentoPagador', 'codigoFalanteRadar', 'transactionID',
    'valor', 'valorString', 'valorLiquido', 'valorLiquidoString', 'valorTotal', 'valorJuros', 'multa', 'desconto',
    'statusCobranca', 'statusCobrancaTexto', 'vencimento', 'vencimentoString', 'expiracao', 'expirada', 'pagamento', 'pagamentoString',
    'totalParcelas', 'parcela', 'parcelas', 'recorrenciaID', 'formaDePagamento',
    'descricao', 'descricao2', 'observacaoInterna', 'resumoDeVendas',
    'emailPagador', 'telefonePagador', 'DDD', 'NumeroTelefone', 'uf',
    'cobradorNome', 'cobradorDocumento', 'geradoPor', 'comissionadoResponsavel',
    'comissoesCobranca',
    'Franqueado', 'FranqueadoPorcentagem', 'AssistenteComercial', 'AssistenteComercialPorcentagem',
    'Advogado', 'AdvogadoPorcentagem', 'TemEspelhamento', 'grupoOgms',
    'linkCobranca', 'linkBoleto', 'linkContrato', 'linkContratoAssinado', 'linkContratoAditivo',
    'linkContratoAditivoAssinado', 'amazonLink', 'parceiroLink',
    'indicadorPersonalizadoParceiro', 'boletoCobrancaID', 'linkPagamentoID', 'gerarNfe', 'processado_em',
    'dateDesconto', 'dataDeDescontoString', 'dateLimiteDePagamento', 'dataLimiteDePagamentoString'
  ]);

  // Função para verificar se há campos com valores
  const hasFieldsWithValues = (fields, data) => {
    return fields.some(field => {
      let value;
      if (field.name.includes('.')) {
        const parts = field.name.split('.');
        value = data[parts[0]]?.[parts[1]];
      } else {
        value = data[field.name];
      }
      return value !== null && value !== undefined && value !== '';
    });
  };

  return (
    <>
      {/* Informações Principais */}
      {hasFieldsWithValues([
        { name: 'nomePagador' },
        { name: 'cobrancaID' },
        { name: 'id' },
        { name: 'documentoPagador' },
        { name: 'codigoFalanteRadar' },
        { name: 'transactionID' }
      ], data) && renderFieldSection('Informações Principais', [
        { name: 'nomePagador', label: 'Nome do Pagador', type: 'string' },
        { name: 'cobrancaID', label: 'ID da Cobrança', type: 'number' },
        { name: 'id', label: 'ID', type: 'number' },
        { name: 'documentoPagador', label: 'Documento do Pagador', type: 'string' },
        { name: 'codigoFalanteRadar', label: 'Código Falante Radar', type: 'string' },
        { name: 'transactionID', label: 'Transaction ID', type: 'string' }
      ], data)}

      {/* Valores e Financeiro */}
      {hasFieldsWithValues([
        { name: 'valor' },
        { name: 'valorString' },
        { name: 'valorLiquido' },
        { name: 'valorLiquidoString' },
        { name: 'valorTotal' },
        { name: 'valorJuros' },
        { name: 'multa' },
        { name: 'desconto' }
      ], data) && renderFieldSection('Valores e Financeiro', [
        { name: 'valor', label: 'Valor', type: 'currency' },
        { name: 'valorString', label: 'Valor Formatado', type: 'string' },
        { name: 'valorLiquido', label: 'Valor Líquido', type: 'currency' },
        { name: 'valorLiquidoString', label: 'Valor Líquido Formatado', type: 'string' },
        { name: 'valorTotal', label: 'Valor Total', type: 'currency' },
        { name: 'valorJuros', label: 'Valor Juros', type: 'currency' },
        { name: 'multa', label: 'Multa', type: 'currency' },
        { name: 'desconto', label: 'Desconto', type: 'currency' }
      ], data)}

      {/* Status e Vencimento */}
      {hasFieldsWithValues([
        { name: 'statusCobranca' },
        { name: 'statusCobrancaTexto' },
        { name: 'vencimento' },
        { name: 'vencimentoString' },
        { name: 'expiracao' },
        { name: 'expirada' },
        { name: 'pagamento' },
        { name: 'pagamentoString' }
      ], data) && renderFieldSection('Status e Vencimento', [
        { name: 'statusCobranca', label: 'Status da Cobrança', type: 'number' },
        { name: 'statusCobrancaTexto', label: 'Status Texto', type: 'status' },
        { name: 'vencimento', label: 'Vencimento', type: 'date' },
        { name: 'vencimentoString', label: 'Vencimento (BR)', type: 'string' },
        { name: 'expiracao', label: 'Expiração', type: 'date' },
        { name: 'expirada', label: 'Expirada', type: 'boolean' },
        { name: 'pagamento', label: 'Data do Pagamento', type: 'date' },
        { name: 'pagamentoString', label: 'Pagamento (BR)', type: 'string' }
      ], data)}

      {/* Parcelas */}
      {hasFieldsWithValues([
        { name: 'totalParcelas' },
        { name: 'parcela' },
        { name: 'parcelas' },
        { name: 'recorrenciaID' },
        { name: 'formaDePagamento' }
      ], data) && renderFieldSection('Parcelas', [
        { name: 'totalParcelas', label: 'Total de Parcelas', type: 'number' },
        { name: 'parcela', label: 'Parcela Atual', type: 'number' },
        { name: 'parcelas', label: 'Parcelas', type: 'string' },
        { name: 'recorrenciaID', label: 'ID da Recorrência', type: 'number' },
        { name: 'formaDePagamento', label: 'Forma de Pagamento', type: 'string' }
      ], data)}

      {/* Descrições */}
      {hasFieldsWithValues([
        { name: 'descricao' },
        { name: 'descricao2' },
        { name: 'observacaoInterna' },
        { name: 'resumoDeVendas' }
      ], data) && renderFieldSection('Descrições', [
        { name: 'descricao', label: 'Descrição', type: 'string' },
        { name: 'descricao2', label: 'Descrição 2', type: 'string' },
        { name: 'observacaoInterna', label: 'Observação Interna', type: 'string' },
        { name: 'resumoDeVendas', label: 'Resumo de Vendas', type: 'string' }
      ], data)}

      {/* Contato */}
      {hasFieldsWithValues([
        { name: 'emailPagador' },
        { name: 'telefonePagador' },
        { name: 'DDD' },
        { name: 'NumeroTelefone' },
        { name: 'uf' }
      ], data) && renderFieldSection('Contato', [
        { name: 'emailPagador', label: 'Email do Pagador', type: 'string' },
        { name: 'telefonePagador', label: 'Telefone do Pagador', type: 'string' },
        { name: 'DDD', label: 'DDD', type: 'string' },
        { name: 'NumeroTelefone', label: 'Número do Telefone', type: 'string' },
        { name: 'uf', label: 'UF', type: 'string' }
      ], data)}

      {/* Cobrador */}
      {hasFieldsWithValues([
        { name: 'cobradorNome' },
        { name: 'cobradorDocumento' },
        { name: 'geradoPor' },
        { name: 'comissionadoResponsavel' }
      ], data) && renderFieldSection('Cobrador', [
        { name: 'cobradorNome', label: 'Nome do Cobrador', type: 'string' },
        { name: 'cobradorDocumento', label: 'Documento do Cobrador', type: 'string' },
        { name: 'geradoPor', label: 'Gerado Por', type: 'string' },
        { name: 'comissionadoResponsavel', label: 'Comissionado Responsável', type: 'string' }
      ], data)}

      {/* Comissões */}
      {renderComissoes(data.comissoesCobranca)}

      {/* Franquia e Comercial */}
      {hasFieldsWithValues([
        { name: 'Franqueado' },
        { name: 'FranqueadoPorcentagem' },
        { name: 'AssistenteComercial' },
        { name: 'AssistenteComercialPorcentagem' },
        { name: 'Advogado' },
        { name: 'AdvogadoPorcentagem' },
        { name: 'TemEspelhamento' },
        { name: 'grupoOgms' }
      ], data) && renderFieldSection('Franquia e Comercial', [
        { name: 'Franqueado', label: 'Franqueado', type: 'string' },
        { name: 'FranqueadoPorcentagem', label: '% Franqueado', type: 'number' },
        { name: 'AssistenteComercial', label: 'Assistente Comercial', type: 'string' },
        { name: 'AssistenteComercialPorcentagem', label: '% Assistente', type: 'number' },
        { name: 'Advogado', label: 'Advogado', type: 'string' },
        { name: 'AdvogadoPorcentagem', label: '% Advogado', type: 'number' },
        { name: 'TemEspelhamento', label: 'Tem Espelhamento', type: 'boolean' },
        { name: 'grupoOgms', label: 'Grupo OGMS', type: 'string' }
      ], data)}

      {/* Links */}
      {hasFieldsWithValues([
        { name: 'linkCobranca' },
        { name: 'linkBoleto' },
        { name: 'linkContrato' },
        { name: 'linkContratoAssinado' },
        { name: 'linkContratoAditivo' },
        { name: 'linkContratoAditivoAssinado' },
        { name: 'amazonLink' },
        { name: 'parceiroLink' }
      ], data) && renderFieldSection('Links', [
        { name: 'linkCobranca', label: 'Link da Cobrança', type: 'url' },
        { name: 'linkBoleto', label: 'Link do Boleto', type: 'url' },
        { name: 'linkContrato', label: 'Link do Contrato', type: 'url' },
        { name: 'linkContratoAssinado', label: 'Contrato Assinado', type: 'url' },
        { name: 'linkContratoAditivo', label: 'Contrato Aditivo', type: 'url' },
        { name: 'linkContratoAditivoAssinado', label: 'Aditivo Assinado', type: 'url' },
        { name: 'amazonLink', label: 'Link Amazon', type: 'url' },
        { name: 'parceiroLink', label: 'Link Parceiro', type: 'url' }
      ], data)}

      {/* Outros Campos */}
      {hasFieldsWithValues([
        { name: 'indicadorPersonalizadoParceiro' },
        { name: 'boletoCobrancaID' },
        { name: 'linkPagamentoID' },
        { name: 'gerarNfe' },
        { name: 'processado_em' }
      ], data) && renderFieldSection('Outros Campos', [
        { name: 'indicadorPersonalizadoParceiro', label: 'Indicador Parceiro', type: 'string' },
        { name: 'boletoCobrancaID', label: 'ID Boleto Cobrança', type: 'string' },
        { name: 'linkPagamentoID', label: 'ID Link Pagamento', type: 'string' },
        { name: 'gerarNfe', label: 'Gerar NFE', type: 'string' },
        { name: 'processado_em', label: 'Processado em', type: 'date' }
      ], data)}

      {/* Datas de Desconto */}
      {hasFieldsWithValues([
        { name: 'dateDesconto' },
        { name: 'dataDeDescontoString' },
        { name: 'dateLimiteDePagamento' },
        { name: 'dataLimiteDePagamentoString' }
      ], data) && renderFieldSection('Datas de Desconto', [
        { name: 'dateDesconto', label: 'Data do Desconto', type: 'date' },
        { name: 'dataDeDescontoString', label: 'Data do Desconto (BR)', type: 'string' },
        { name: 'dateLimiteDePagamento', label: 'Data Limite de Pagamento', type: 'date' },
        { name: 'dataLimiteDePagamentoString', label: 'Data Limite (BR)', type: 'string' }
      ], data)}

      {/* Renderizar TODOS os campos que não foram incluídos nas seções anteriores */}
      {(() => {
        // Filtrar campos desconhecidos
        const unknownFields = Object.keys(data).filter(key => !knownFields.has(key));
        
        if (unknownFields.length === 0) return null;

        console.log('📊 Campos desconhecidos encontrados:', unknownFields);

        return (
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 mb-4">
            <div className="bg-yellow-100 px-4 py-2 border-b border-yellow-200">
              <h4 className="font-semibold text-yellow-800">🔍 Outros Campos ({unknownFields.length})</h4>
            </div>
            <div className="p-4 space-y-1">
              {unknownFields.map((key, index) => {
                const value = data[key];
                
                // Determinar o tipo do campo automaticamente
                let type = 'string';
                if (typeof value === 'number') type = 'number';
                if (typeof value === 'boolean') type = 'boolean';
                if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) type = 'url';
                if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) type = 'date';
                
                // Criar label legível a partir do nome do campo
                const label = key
                  .replace(/_/g, ' ')
                  .replace(/([A-Z])/g, ' $1')
                  .trim()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ');

                return (
                  <div key={`unknown-${key}-${index}`}>
                    {renderJsonField(label, value, type)}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </>
  );
};
